/**
 * FASE 3 — HISTÓRICO DE GRADUAÇÃO (aba "Graduação" da ficha do atleta).
 *
 * Mostra a linha do tempo (do mais recente para o mais antigo) e permite
 * REGISTRAR uma graduação nova. Sem GRAU — decisão do usuário: grau varia demais
 * de equipe para equipe.
 *
 * O servidor é quem manda: registrar/ apagar um registro faz ele recalcular a
 * faixa atual do atleta a partir do registro MAIS RECENTE. Esta tela só mostra o
 * que voltou e avisa o usuário (toast) — nada é recalculado no frontend.
 */
import { useCallback, useEffect, useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';
import { AlertTriangle, History, Loader2, Plus, Trash2 } from 'lucide-react';
import { athleteApi, userApi } from '../lib/api';
import { formatarDataBr, hojeIso, tempoNaFaixa, ordenarGraduacoes } from '../lib/graduacao';

export default function HistoricoGraduacao({ athleteId, belt, beltSince, belts = [], onMudou }) {
  const [registros, setRegistros] = useState([]);
  const [carregando, setCarregando] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [form, setForm] = useState({ belt: '', date: '', professor_id: '', notes: '' });
  const [professores, setProfessores] = useState([]);
  // Fase 2 (item 8 / opção B): mensagem de erro mostrada junto ao campo.
  const [erros, setErros] = useState({});

  const carregar = useCallback(async () => {
    if (!athleteId) return;
    setCarregando(true);
    try {
      const { data } = await athleteApi.graduations(athleteId);
      setRegistros(ordenarGraduacoes(data.graduations || []));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erro ao carregar o histórico');
    } finally {
      setCarregando(false);
    }
  }, [athleteId]);

  useEffect(() => { carregar(); }, [carregar]);

  useEffect(() => {
    userApi.list({ role: 'professor' })
      .then((r) => setProfessores(r.data || []))
      .catch(() => setProfessores([]));
  }, []);

  const registrar = async () => {
    if (!form.belt) { setErros((atual) => ({ ...atual, belt: 'Escolha a faixa da graduação' })); return; }
    if (!form.date) { setErros((atual) => ({ ...atual, date: 'Informe a data da graduação' })); return; }
    setSalvando(true);
    try {
      const { data } = await athleteApi.addGraduation(athleteId, {
        belt: form.belt,
        date: form.date,
        professor_id: form.professor_id || null,
        notes: form.notes || null,
      });
      (data.warnings || []).forEach((w) => toast.warning(w));
      toast.success(
        data.atualizou_faixa
          ? `Graduação registrada — faixa atual do atleta: ${data.athlete_belt}`
          : 'Graduação registrada no histórico',
      );
      setForm({ belt: '', date: '', professor_id: '', notes: '' });
      setErros({});
      await carregar();
      if (onMudou) onMudou(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erro ao registrar a graduação');
    } finally {
      setSalvando(false);
    }
  };

  const apagar = async (registro) => {
    if (!window.confirm(
      `Apagar o registro de ${registro.belt} (${formatarDataBr(registro.date)})?`,
    )) return;
    try {
      const { data } = await athleteApi.removeGraduation(athleteId, registro.id);
      (data.warnings || []).forEach((w) => toast.warning(w));
      toast.success('Registro apagado');
      await carregar();
      if (onMudou) onMudou(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erro ao apagar o registro');
    }
  };

  const corDaFaixa = (nome) => belts.find((b) => b.name === nome)?.color_code || '#71717A';

  return (
    <div className="space-y-4" data-testid="historico-graduacao">
      {/* ---- Linha do tempo ---- */}
      <div className="space-y-2">
        <p className="text-micro text-[#A1A1AA]">
          Faixa atual: <strong className="text-white">{belt || '—'}</strong>
          {beltSince ? ` · desde ${formatarDataBr(beltSince)}` : ' · sem data de graduação'}
          {beltSince && tempoNaFaixa(beltSince) ? ` (${tempoNaFaixa(beltSince)})` : ''}
        </p>

        {carregando ? (
          <p className="text-micro text-[#71717A] flex items-center gap-2">
            <Loader2 className="w-3 h-3 animate-spin" /> Carregando histórico...
          </p>
        ) : registros.length === 0 ? (
          <p className="text-micro text-[#71717A] flex items-center gap-2" data-testid="historico-vazio">
            <History className="w-3 h-3" /> Nenhuma graduação registrada ainda.
            A faixa que está no cadastro continua valendo.
          </p>
        ) : (
          <ul className="space-y-1.5" data-testid="historico-lista">
            {registros.map((r, i) => (
              <li
                key={r.id}
                data-testid={`historico-item-${i}`}
                className="flex items-center justify-between gap-3 rounded-md border border-[#27272A] bg-[#141414] px-3 py-2"
              >
                <span className="flex items-center gap-2 min-w-0">
                  <span
                    className="w-3 h-3 rounded-full shrink-0"
                    style={{ backgroundColor: corDaFaixa(r.belt), border: '1px solid #555' }}
                  />
                  <span className="text-xs text-white font-medium">{r.belt}</span>
                  <span className="text-micro text-[#A1A1AA]">
                    {formatarDataBr(r.date)}
                  </span>
                  {r.professor_name && (
                    <span className="text-micro text-[#71717A] truncate">
                      · {r.professor_name}
                    </span>
                  )}
                  {i === 0 && (
                    <span className="text-micro text-[#D4AF37] border border-[#D4AF37]/40 rounded px-1">
                      mais recente
                    </span>
                  )}
                </span>
                <button
                  type="button"
                  onClick={() => apagar(r)}
                  data-testid={`historico-apagar-${i}`}
                  title="Apagar registro"
                  className="text-[#71717A] hover:text-red-400 shrink-0"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </li>
            ))}
          </ul>
        )}
        {registros.length > 0 && (
          <p className="text-micro text-[#71717A] flex items-start gap-1">
            <AlertTriangle className="w-3 h-3 shrink-0 mt-px" />
            A faixa atual do atleta é sempre a do registro mais recente. Registrar uma
            graduação antiga não rebaixa a faixa; apagar a mais recente volta para a anterior.
          </p>
        )}
      </div>

      {/* ---- Registrar graduação ---- */}
      <div className="rounded-md border border-[#27272A] bg-[#0F0F0F] p-3 space-y-3">
        <p className="text-micro font-bold text-[#D4AF37] uppercase tracking-wider flex items-center gap-2">
          <Plus className="w-3 h-3" /> Registrar graduação
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label className="text-[#A1A1AA] text-xs">Faixa</Label>
            <Select
              value={form.belt || 'none'}
              onValueChange={(v) => {
                if (erros.belt) setErros((atual) => ({ ...atual, belt: undefined }));
                setForm({ ...form, belt: v === 'none' ? '' : v });
              }}
            >
              <SelectTrigger
                data-testid="graduacao-belt"
                aria-invalid={erros.belt ? true : undefined}
                className="bg-[#1A1A1A] border-[#27272A] text-white"
              >
                <SelectValue placeholder="Selecione..." />
              </SelectTrigger>
              <SelectContent className="bg-[#1A1A1A] border-[#27272A]">
                <SelectItem value="none" className="text-white">Selecione...</SelectItem>
                {belts.map((b) => (
                  <SelectItem key={b._id || b.name} value={b.name} className="text-white hover:bg-[#27272A]">
                    {b.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {erros.belt && (
              <p role="alert" className="text-xs text-red-400 mt-1">{erros.belt}</p>
            )}
          </div>
          <div className="space-y-1.5">
            <Label className="text-[#A1A1AA] text-xs">Data</Label>
            <Input
              type="date"
              value={form.date}
              max={hojeIso()}
              onChange={(e) => {
                if (erros.date) setErros((atual) => ({ ...atual, date: undefined }));
                setForm({ ...form, date: e.target.value });
              }}
              data-testid="graduacao-date"
              aria-invalid={erros.date ? true : undefined}
              className="bg-[#1A1A1A] border-[#27272A] text-white"
            />
            {erros.date && (
              <p role="alert" className="text-xs text-red-400 mt-1">{erros.date}</p>
            )}
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label className="text-[#A1A1AA] text-xs">Quem graduou (opcional)</Label>
            <Select value={form.professor_id || 'none'} onValueChange={(v) => setForm({ ...form, professor_id: v === 'none' ? '' : v })}>
              <SelectTrigger data-testid="graduacao-professor" className="bg-[#1A1A1A] border-[#27272A] text-white">
                <SelectValue placeholder="Selecione..." />
              </SelectTrigger>
              <SelectContent className="bg-[#1A1A1A] border-[#27272A]">
                <SelectItem value="none" className="text-white">—</SelectItem>
                {professores.map((p) => (
                  <SelectItem key={p._id} value={p._id} className="text-white hover:bg-[#27272A]">{p.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-[#A1A1AA] text-xs">Observação (opcional)</Label>
            <Input
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              placeholder="Ex: seminário, exame de faixa..."
              data-testid="graduacao-notes"
              className="bg-[#1A1A1A] border-[#27272A] text-white"
            />
          </div>
        </div>
        <Button
          type="button"
          onClick={registrar}
          disabled={salvando}
          data-testid="graduacao-salvar"
          className="bg-[#D4AF37] text-black hover:bg-[#E5C158] font-bold"
        >
          {salvando ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
          Registrar
        </Button>
        <p className="text-micro text-[#71717A]">
          Sem grau: o sistema guarda a faixa e a data. Não existe grau (0 a 4) porque
          isso varia de equipe para equipe.
        </p>
      </div>
    </div>
  );
}
